import React, { useState, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/**
 * GoalStatusSelector - A reusable status selector with transition logic and change dialog
 *
 * @param {string} currentStatusId - The current status ID
 * @param {Array} statuses - Array of all available statuses [{id, name, color}]
 * @param {Array} transitions - Array of status transitions [{fromStatusId, toStatusId, isAllowed}]
 * @param {Function} onStatusChange - Callback when status changes (newStatusId, newStatus, reason) => void
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {boolean} fullWidth - Whether to use full width
 * @param {string} label - Label for the selector
 */
const GoalStatusSelector = ({
  currentStatusId,
  statuses = [],
  transitions = [],
  onStatusChange,
  disabled = false,
  fullWidth = true,
  label = 'Status',
}) => {
  const [statusChangeDialog, setStatusChangeDialog] = useState({
    open: false,
    newStatusId: null,
    newStatus: null,
    notes: '',
  });

  // Get current status object
  const currentStatus = useMemo(() => {
    return statuses.find(s => s.id === currentStatusId);
  }, [statuses, currentStatusId]);

  // Get available statuses based on transitions from the current status
  const availableStatuses = useMemo(() => {
    // If no current status or no transitions configured, show all statuses
    if (!currentStatusId || !transitions || transitions.length === 0) {
      return statuses;
    }

    // Find allowed transitions FROM the current status (forward transitions)
    const forwardTransitions = transitions.filter(
      t => t.fromStatusId === currentStatusId && t.isAllowed
    );

    // Find allowed transitions TO the current status (reverse transitions - allows going back)
    const reverseTransitions = transitions.filter(
      t => t.toStatusId === currentStatusId && t.isAllowed
    );

    // Get the IDs of statuses we can transition to (forward)
    const forwardStatusIds = forwardTransitions.map(t => t.toStatusId);

    // Get the IDs of statuses we can transition back to (reverse)
    const reverseStatusIds = reverseTransitions.map(t => t.fromStatusId);

    // Combine both forward and reverse transitions
    const allowedStatusIds = [...new Set([...forwardStatusIds, ...reverseStatusIds])];

    // Filter statuses to only include current status and allowed transitions (both directions)
    return statuses.filter(
      status => status.id === currentStatusId || allowedStatusIds.includes(status.id)
    );
  }, [statuses, transitions, currentStatusId]);

  const handleStatusChange = (event) => {
    const newStatusId = event.target.value;

    // If selecting the same status, don't open dialog
    if (newStatusId === currentStatusId) {
      return;
    }

    const newStatus = statuses.find(s => s.id === newStatusId);

    // Open dialog to add notes for the status change
    setStatusChangeDialog({
      open: true,
      newStatusId,
      newStatus,
      notes: '',
    });
  };

  const handleStatusChangeConfirm = () => {
    onStatusChange(
      statusChangeDialog.newStatusId,
      statusChangeDialog.newStatus,
      statusChangeDialog.notes
    );
    setStatusChangeDialog({
      open: false,
      newStatusId: null,
      newStatus: null,
      notes: '',
    });
  };

  const handleStatusChangeCancel = () => {
    setStatusChangeDialog({
      open: false,
      newStatusId: null,
      newStatus: null,
      notes: '',
    });
  };

  return (
    <>
      <FormControl fullWidth={fullWidth}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={currentStatusId || ''}
          onChange={handleStatusChange}
          label={label}
          disabled={disabled}
        >
          {availableStatuses.map((status) => (
            <MenuItem key={status.id} value={status.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: status.color || '#6b7280',
                  }}
                />
                {status.name}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status Change Dialog */}
      <Dialog
        open={statusChangeDialog.open}
        onClose={handleStatusChangeCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Status</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: 1 }}>
            {currentStatus && (
              <Chip
                label={currentStatus.name}
                sx={{
                  backgroundColor: currentStatus.color || '#6b7280',
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            )}
            <ArrowForwardIcon sx={{ color: '#9CA3AF' }} />
            {statusChangeDialog.newStatus && (
              <Chip
                label={statusChangeDialog.newStatus.name}
                sx={{
                  backgroundColor: statusChangeDialog.newStatus.color || '#6b7280',
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
          <TextField
            label="Notes (optional)"
            value={statusChangeDialog.notes}
            onChange={(e) => setStatusChangeDialog(prev => ({ ...prev, notes: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            placeholder="Add any notes about this status change..."
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusChangeCancel}>Cancel</Button>
          <Button
            onClick={handleStatusChangeConfirm}
            variant="contained"
            color="primary"
          >
            Change Status
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoalStatusSelector;
